import { useEffect, useState, memo } from "react";
import { Bar } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";

Chart.register(CategoryScale);

export const SectorBasedAverageproductSoldChart = memo(() => {
    const currentTime = new Date();
    const currentYear = currentTime.getFullYear();
    const currentMonth = currentTime.getUTCMonth() + 1;

    const [filter, setFilter] = useState("monthly");
    const getCurrentFiscalYear = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1;
        // Fiscal year starts in September
        const startYear = month >= 9 ? year : year - 1;
        const endYear = startYear + 1;
        return `${startYear}-${endYear}`;
    };
    const [period, setPeriod] = useState(getCurrentFiscalYear());
    const [sector, setSector] = useState("Education - Secondary Schools");
    const [isLoading, setIsLoading] = useState(true); 
    const [sectorOptions, setSectorOptions] = useState([]);
    const [topSellerChartData, setTopSellerChartData] = useState({ labels: [], datasets: [] });
    const [sectorsLoaded, setSectorsLoaded] = useState(false);


    const noDataPlugin = {
        id: "noDataPlugin",
        beforeDraw: (chart) => {
            const { datasets } = chart.data;
            const hasData = datasets.some((dataset) => dataset.data.length > 0);
            if (!hasData) {
                const ctx = chart.ctx;
                const { width, height } = chart;
                ctx.save();
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.font = "16px Arial";
                ctx.fillStyle = "#eb9f9d";
                ctx.fillText("Sorry, no data found for your filtered search.", width / 2, height / 2);
                ctx.restore();
            }
        },
    };
    
    const sectorTitlePlugin = {
        id: "sectorTitlePlugin",
        
        layout: {
            padding: {
              top: 100, // or higher if needed
              right: 0,
              bottom: 50,
              left: 0
            }
          },
        beforeDraw: (chart) => {
            const sector = chart.options.plugins.sectorTitle?.text;
            if (!sector) return;
            const { ctx, chartArea: { top, left, right } } = chart;
            ctx.save();
            ctx.font = "bold 16px Arial";
            ctx.fillStyle = "#333";
            ctx.textAlign = "center";
            ctx.fillText(sector, (left + right) / 2, top - 55); // draw above chart area
            ctx.restore();
        },
    };

    const fiscalQuarters = { 1: "Sep-Nov", 2: "Dec-Feb", 3: "Mar-May", 4: "Jun-Aug" };

    const generateFiscalYearOptions = (currentYear, currentMonth, startYear = 2023) => {
        const options = [];
        for (let i = currentYear; i >= startYear; i--) {
            if (i === currentYear && currentMonth < 9) continue;
            const label = `${i}-${i + 1}`;
            options.push(<option key={i} value={label}>{label}</option>);
        }
        return options;
    };

    useEffect(() => {
        const fetchSectors = async () => {
            try {
                const response = await http.get(BaseUrl + "admin/account");
                const accountsData = response.data.data;
                const uniqueSectors = [...new Set(accountsData.map((acc) => acc.Sectors).filter((s) => s?.trim()))];
                const defaultSector = "Education - Secondary Schools";
                //default sector at the top of the list
                const orderedSectors = [
                    defaultSector,
                    ...uniqueSectors.filter(s => s !== defaultSector)
                ];
                setSectorOptions(orderedSectors);
                // sector state is set to the default if it's valid
                if (!uniqueSectors.includes(sector)) {
                    setSector(defaultSector);
                }
                setSectorsLoaded(true);
            } catch (error) {
                console.error("Error fetching sectors:", error);
            }
        };
        fetchSectors();
    }, []);
    // Function to format the date as dd-mm-yyyy
    const formatDate = (date) => {
        const d = new Date(date);
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

   useEffect(() => {
    if (!sectorsLoaded) return;

    setIsLoading(true);
    const URL = BaseUrl + "admin/sectorbasedaverageproductsold";

    http.post(URL, { period, filter, sector }).then((response) => {
        setIsLoading(false);
        const rawData = response.data.data;
        const sectorData = rawData;
        // Arrays to store the final chart data
        const labels = []; // X-axis labels
        const avgTotalData = [];  // Average total product sold (all categories)
        const pizzaData = []; // Average pizza sold
        const pastaData = []; // Average pasta sold
        const burritoData = []; // Average burrito sold
        const dessertsData = []; // Average desserts sold
        //console.log("Burrito data:", burritoData);
        // map category IDs to names
        const mapCategory = (id) => {
            if (id === "1") return "pizza";
            if (["10", "13"].includes(id)) return "pasta";
            if (["11", "12"].includes(id)) return "burrito";
            if (id === "14") return "desserts";
            return null;
        };
        // Decide which field to group data by (date/month/quarter/year)
        const groupKey = filter === "weekly" ? "date"
                       : filter === "monthly" ? "month"
                       : filter === "quarterly" ? "quarter"
                       : filter === "FYdata" ? "year"
                       : null;
        // Group the data by that key (ex: all Jan data together)
        const grouped = {};
        sectorData.forEach(item => {
            const key = groupKey ? item._id[groupKey] : "all";
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(item);
        });
        // starts from sep(fiscal order)
        let sortedKeys = Object.keys(grouped);
        if (filter === "monthly") {
            const fiscalMonths = [9,10,11,12,1,2,3,4,5,6,7,8];
            sortedKeys.sort((a, b) => fiscalMonths.indexOf(+a) - fiscalMonths.indexOf(+b));
        } else {
            sortedKeys.sort();
        }

        sortedKeys.forEach(key => {
            const item = grouped[key][0];
            // Calculate average total products sold
            const totalOrders = item.totalOrders || 0;
            const totalQuantity = item.totalQuantityInSector || 0;
            const avgTotal = totalOrders > 0 ? totalQuantity / totalOrders : 0;

            const categories = { pizza: 0, pasta: 0, burrito: 0 , desserts: 0};
             // Loop through product categories and sum quantities by type
            (item.categories || []).forEach(cat => {
                const type = mapCategory(cat.category);
                if (type) categories[type] += cat.totalQuantity || 0;
            });
            // Calculate average per product type
            const avgPizza = totalOrders > 0 ? categories.pizza / totalOrders : 0;
            const avgPasta = totalOrders > 0 ? categories.pasta / totalOrders : 0;
            const avgBurrito = totalOrders > 0 ? categories.burrito / totalOrders : 0;
            const avgDessert = totalOrders > 0 ? categories.desserts / totalOrders : 0;
            // Create label for chart 
            let label;
            if (filter === "weekly") {
                const rawDate = item._id.date;
                label = formatDate(rawDate);
            } else if (filter === "monthly") {
                label = new Date(0, +key - 1).toLocaleString("default", { month: "short" });
            } else if (filter === "quarterly") {
                label = `${fiscalQuarters[item._id.quarter]}`;
            } else if (filter === "FYdata") {
                label = period;
            }
            // Push values to the arrays
            labels.push(label);
            avgTotalData.push({ x: label, y: avgTotal, totalOrders, totalQuantityInSector: totalQuantity });
            pizzaData.push({ x: label, y: avgPizza, totalOrders, totalQuantity: categories.pizza });
            pastaData.push({ x: label, y: avgPasta, totalOrders, totalQuantity: categories.pasta });
            burritoData.push({ x: label, y: avgBurrito, totalOrders, totalQuantity: categories.burrito });
            dessertsData.push({ x: label, y: avgDessert, totalOrders, totalQuantity: categories.desserts });
        });
        // Prepare final chart data object
        const chartData = {
            labels,
            datasets: [
                { label: "Avg. Total Products Sold (All Categories)", data: avgTotalData, backgroundColor: "#1b81bc",barThickness: 17 },
                { label: "Avg. Pizza Products Sold", data: pizzaData, backgroundColor: "#4ca8d9",barThickness: 17 },
                { label: "Avg. Pasta Products Sold", data: pastaData, backgroundColor: "#8dc5e4", barThickness: 17 },
                { label: "Avg. Burrito Products Sold", data: burritoData, backgroundColor:"#70dbd8", barThickness: 17 },
                { label: "Avg. Dessert Products Sold", data: dessertsData, backgroundColor: "#C3B7FB", barThickness: 17 },
            ]
        };
        // Update the chart state to show
        setTopSellerChartData(chartData);
    }).catch((err) => {
        console.error(err);
        setIsLoading(false);
    });
}, [filter, period, sector, sectorsLoaded]);

    return (
        <div className={`chart-container chart-revenue ${isLoading ? 'loading-buffer' : ''}`}>
            <div className="chart-Hdr">
                <h2 style={{ textAlign: "left" }}>Average Products Sold by Sector</h2>
                <div className="chart-fltr">
                    <select style={{ width: '' }} value={sector} onChange={(e) => setSector(e.target.value)}>
                        {sectorOptions.map((sector, index) => (
                            <option key={index} value={sector}>{sector}</option>
                        ))}
                    </select>
                    <select style={{ width: '150px' }} value={filter} onChange={(e) => setFilter(e.target.value)}>
                        <option value="weekly">Current Week</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="FYdata">FY Data</option>
                    </select>
                    {(filter === 'monthly' || filter === 'quarterly' || filter === 'FYdata') && (
                        <select style={{ width: '170px' }} value={period} onChange={(e) => setPeriod(e.target.value)}>
                            {generateFiscalYearOptions(currentYear, currentMonth)}
                        </select>
                    )}
                </div>
            </div>
            {topSellerChartData && (
                <Bar
                    data={topSellerChartData}
                    plugins={[noDataPlugin,sectorTitlePlugin]}
                    options={{
                        scales: {
                            y: {
                                grid: { color: 'rgba(247, 243, 241, 0.8)' },
                                ticks: { color: 'rgba(207, 208, 211, 1)' }
                            },
                            x: {
                                grid: { color: 'rgba(247, 243, 241, 0.8)' },
                                categoryPercentage: 0.8,   // controls total group width
                                barPercentage: 0.9,        // controls width of each bar within the group
                                ticks: {
                                    color: 'rgba(207, 208, 211, 1)',
                                    callback(value) {
                                        const label = this.getLabelForValue(value);
                                        if (typeof label === 'string' && label.includes(" ")) {
                                            const [date, dayOfWeek] = label.split(" ");
                                            return [dayOfWeek, date];
                                        }
                                        return label;
                                    },
                                } 
                            }
                        },
                        layout: { padding: { top: 30, right: 0, bottom: 20, left: 0 } },
                        plugins: {
                            sectorTitle: {
                                  text: sector // this should be a prop or state value
                                },
                            title: { display: true },
                            legend: { display: true, position: "top", labels: {boxWidth: 20}, padding: 20 },
                            tooltip: {
                                titleColor: 'rgba(220, 222, 226, 1)',
                                bodyColor: 'rgba(220, 222, 226, 1)',
                                callbacks: {
                                    label: function (context) {
                                        const dataset = context.dataset;
                                        const index = context.dataIndex;
                                        const value = dataset.data?.[index];
                                        const meta = dataset.meta?.[index] || {};
                                        const valueY = value?.y ?? 0;
                                        const valueStr = valueY.toString();
                                        const valueDecimal = valueStr.split(".")[1];
                                        const valueFractionDigits = valueDecimal
                                            ? Math.min(valueDecimal.length, 2)
                                            : 0;
                                       const formattedValue = Number(valueY).toLocaleString("en-GB", {
                                            minimumFractionDigits: valueFractionDigits,
                                            maximumFractionDigits: valueFractionDigits
                                        });

                                        const amount = meta.amount ?? 0;
                                        const amountStr = amount.toString();
                                        const amountDecimal = amountStr.split(".")[1];
                                        const amountFractionDigits = amountDecimal
                                            ? Math.min(amountDecimal.length, 2)
                                            : 0;
                                        const totalRevenue = Number(amount).toLocaleString("en-GB", {
                                            minimumFractionDigits: amountFractionDigits,
                                            maximumFractionDigits: amountFractionDigits
                                        });
                                        //const lines = [`${dataset.label}: ${formattedValue}`]; // Base line
                                        const raw = context.raw || {};
                                        const lines = [];
                                        // Determine tooltip content based on dataset.label
                                        if (dataset.label === "Avg. Total Products Sold (All Categories)") {
                                            lines.push(`Avg. Total Products Sold (All Categories): ${formattedValue}`);
                                            if (raw.totalQuantityInSector != null) {
                                                lines.push(`Total Products Sold (All Categories): ${Number(raw.totalQuantityInSector).toLocaleString("en-GB")}`);
                                            }
                                            if (raw.totalOrders != null) {
                                                lines.push(`Total Orders: ${Number(raw.totalOrders).toLocaleString("en-GB")}`);
                                            }
                                        } else if (dataset.label === "Avg. Pizza Products Sold") {
                                            lines.push(`Avg. Pizza Products Sold: ${formattedValue}`);
                                            if (raw.totalQuantity != null) {
                                                lines.push(`Total Pizza Products Sold: ${Number(raw.totalQuantity).toLocaleString("en-GB")}`);
                                            }
                                            if (raw.totalOrders != null) {
                                                lines.push(`Total Orders: ${Number(raw.totalOrders).toLocaleString("en-GB")}`);
                                            }
                                        } else if (dataset.label === "Avg. Pasta Products Sold") {
                                            lines.push(`Avg. Pasta Products Sold: ${formattedValue}`);
                                            if (raw.totalQuantity != null) {
                                                lines.push(`Total Pasta Products Sold: ${Number(raw.totalQuantity).toLocaleString("en-GB")}`);
                                            }
                                            if (raw.totalOrders != null) {
                                                lines.push(`Total Orders: ${Number(raw.totalOrders).toLocaleString("en-GB")}`);
                                            }
                                        } else if (dataset.label === "Avg. Burrito Products Sold") {
                                            lines.push(`Avg. Burrito Products Sold: ${formattedValue}`);
                                            if (raw.totalQuantity != null) {
                                                lines.push(`Total Burrito Products Sold: ${Number(raw.totalQuantity).toLocaleString("en-GB")}`);
                                            }
                                            if (raw.totalOrders != null) {
                                                lines.push(`Total Orders: ${Number(raw.totalOrders).toLocaleString("en-GB")}`);
                                            }
                                        } else if (dataset.label === "Avg. Dessert Products Sold") {
                                            lines.push(`Avg. Dessert Products Sold: ${formattedValue}`);
                                            if (raw.totalQuantity != null) {
                                                lines.push(`Total Dessert Products Sold: ${Number(raw.totalQuantity).toLocaleString("en-GB")}`);
                                            }
                                            if (raw.totalOrders != null) {
                                                lines.push(`Total Orders: ${Number(raw.totalOrders).toLocaleString("en-GB")}`);
                                            }
                                        } 

                                        return lines;
                                    }
                                }
                            },
                            datalabels: {
                                display: false,
                                font: { size: 11, weight: '600' },
                                padding: 5,
                                anchor: 'end',
                                align: "top",
                                formatter: value => "£" + value,
                            }
                        },
                    }}
                />
            )}
        </div>
    );
});
